import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PlanDetails } from '@app/_models/planDetails';
import { ConfigDataService } from '@app/_services/config-data.service';
import { first, forkJoin } from 'rxjs';
import { AlertService } from '@app/_services';
import { PrintInstruction } from '@app/_models/printInstruction';
import { PlanDealerAssignmentSummary, PlanCertificateSummary, DealerPlanAssignment } from '@app/_models/plan-dealer-summary';
import { PlanMaintainService } from '../plan-maintain.service';
import { DealerService } from '@app/_services/dealer-service';
import { Dealer } from '@app/_models/dealer';

interface PlanViewModel extends PlanDetails {
  dealerAssignments: DealerPlanAssignment[];
  lastCertificate: PlanCertificateSummary | null;
  dealerCount: number;
  printInstruction?: PrintInstruction | null;
}

interface DealerOption {
  dealerId: string;
  dealerName: string;
  dealerPlanType?: string;
  hasPlan: boolean;
}

interface DrilldownState {
  visible: boolean;
  plan: PlanViewModel | null;
}

interface AssignmentState {
  visible: boolean;
  plan: PlanViewModel | null;
  mode: 'add' | 'replace';
  replacePlanId: string;
  selectedDealerIds: Set<string>;
  submitting: boolean;
  sourcePlan: PlanViewModel | null;
  showConfirmation: boolean;
}

interface PlanComparisonField {
  label: string;
  field: string;
  sourceValue: any;
  targetValue: any;
  isAmount: boolean;
  hasDifference: boolean;
}

@Component({
  selector: 'app-plan-maintain',
  templateUrl: './plan-maintain.component.html',
  styleUrls: ['./plan-maintain.component.less']
})
export class PlanMaintainComponent implements OnInit {
  plans: PlanViewModel[] = [];
  planSummaries: PlanDealerAssignmentSummary[] = [];
  planForm: UntypedFormGroup;
  printInstructions: PrintInstruction[] = [];
  updatePlan = {
    keyValue: "Plan",
    configType: "Array",
    ResultValue: []
  };
  loading = false;
  isEdit = false;
  submitted = false;
  showPlanForm = false;
  applicableOnlyOnPolicyTypes = ["Old","New","Both"];
  booleanArray = ["Yes","No"];
  policyMaintain = [];
  seriesMaintain = [];
  gstPercentage: number;
  drilldown: DrilldownState = { visible: false, plan: null };
  assignment: AssignmentState = {
    visible: false,
    plan: null,
    mode: 'add',
    replacePlanId: '',
    selectedDealerIds: new Set<string>(),
    submitting: false,
    sourcePlan: null,
    showConfirmation: false
  };
  planComparisonFields: PlanComparisonField[] = [];
  hasAmountDifferences = false;
  costExceedsProduct = false;
  assignmentDealerOptions: DealerOption[] = [];
  private allDealersCache: DealerOption[] | null = null;
  isLoadingDealers = false;
  dealerSearch = '';
  planSearchTerm = '';
  policyTypeFilter = '';
  insuranceCompanyFilter = '';
  policyCompanies: string[] = [];

  constructor(
    private configService:ConfigDataService,
    private alertService:AlertService,
    private formBuilder: UntypedFormBuilder,
    private planMaintainService: PlanMaintainService,
    private dealerService: DealerService
  ) { 
    this.planForm = this.formBuilder.group({
      planId: ['', Validators.required],
      planAmount: ['', Validators.required],
      planGstAmount: ['', Validators.required],
      planNetAmount: ['', Validators.required],
      planTotalAmount: ['', Validators.required],
      planMaximumAmount: ['', Validators.required],
      productRackRate: [0],
      operatingCost: [0],
      retentionCost: [0],
      netBenefit: [0],
      policyType: [''],
      gstApplicable: ['', Validators.required],
      gstShow: ['', Validators.required],
      planMinimumAmount: [0],
      rsaNumberOfServices: [0],
      printCpaPolicy: [''],
      planCPACoverAmount: [''],
      printRsaNumber: [''],
      counterId: [''],
      masterPolicyNumber: [''],
      certificatePrefix: [''],
      numberOfYears: [1],
      applicableOnlyOnPolicyType: ["Both"],
      planType: ['General']
    });
    this.applyFormDefaults();
  }

  ngOnInit(): void {
    this.gstPercentage = this.configService.getGST();
    this.configService.SeriesMaintain$.subscribe(seriesMaintain => this.seriesMaintain = seriesMaintain);
    this.configService.MasterPolicyMaintain$.subscribe(policyMaintain => this.policyMaintain = policyMaintain);
    this.loadPolicyCompanies();
    this.fetchPlans();
    this.planForm.get('planTotalAmount').valueChanges.subscribe((value) => {   
      if (value){  
      this.planForm.controls['planGstAmount'].setValue(parseFloat((value - parseFloat((value / (1 + this.gstPercentage)).toFixed(2))).toFixed(2)));
      this.planForm.controls['planAmount'].setValue(parseFloat((value / (1 + this.gstPercentage)).toFixed(2)));
      this.planForm.controls['planMaximumAmount'].setValue(parseFloat((value / (1 + this.gstPercentage)).toFixed(2)));
      this.planForm.controls['planMinimumAmount'].setValue(parseFloat((value / (1 + this.gstPercentage)).toFixed(2)));
    }
    else{
      this.planForm.controls['planGstAmount'].setValue(0);
      this.planForm.controls['planAmount'].setValue(0);
      this.planForm.controls['planMaximumAmount'].setValue(0);
      this.planForm.controls['planMinimumAmount'].setValue(0);
    }

  });

    // Product Rack Rate = Product Cost - Operating Cost - Retention Cost - Net Benefit
    const updateProductRackRate = () => {
      const productCost = this.planForm.get('planNetAmount').value || 0;
      const operatingCost = this.planForm.get('operatingCost').value || 0;
      const netBenefit = this.planForm.get('netBenefit').value || 0;
      const retentionCost = this.planForm.get('retentionCost').value || 0;
      // Validate: operating + retention + net benefit cannot exceed product cost
      this.costExceedsProduct = (operatingCost + retentionCost + netBenefit) > productCost;
      this.planForm.controls['productRackRate'].setValue(parseFloat((productCost - operatingCost - retentionCost - netBenefit).toFixed(2)), { emitEvent: false });
    };
    this.planForm.get('planNetAmount').valueChanges.subscribe(() => updateProductRackRate());
    this.planForm.get('operatingCost').valueChanges.subscribe(() => updateProductRackRate());
    this.planForm.get('netBenefit').valueChanges.subscribe(() => updateProductRackRate());
    this.planForm.get('retentionCost').valueChanges.subscribe(() => updateProductRackRate());
    
    // Auto-populate planType based on planId
    this.planForm.get('planId').valueChanges.subscribe((planId) => {
      if (planId) {
        const planType = planId.toUpperCase().startsWith('AA') ? 'Hero Ensure' : 'General';
        this.planForm.controls['planType'].setValue(planType, { emitEvent: false });
      }
    });
    }
  fetchPlans(): void {
    this.loading = true;
    forkJoin({
      // Use cached endpoint for faster loading
      summaries: this.configService.getCachedPlanDealerAssignments(),
      printInstructions: this.configService.getPrintInstructionList()
    }).pipe(first()).subscribe({
      next: ({ summaries, printInstructions }) => {
        this.printInstructions = printInstructions;
        this.planSummaries = (summaries || []).map(summary => ({
          ...summary,
          plan: new PlanDetails(summary.plan)
        }));
        this.buildPlanViewModels();
        this.loading = false;
      },
      error: error => {
        this.alertService.error(error);
        this.planSummaries = [];
        this.plans = [];
        this.updatePlan.ResultValue = [];
        this.loading = false;
      }
    });
  }

  /**
   * Force sync the plan-dealer mapping cache.
   * Use this after mass changes or when data seems stale.
   */
  syncPlanMappings(): void {
    this.loading = true;
    this.configService.syncPlanDealerMappings().pipe(first()).subscribe({
      next: (result) => {
        this.alertService.success(`Cache sync complete: ${result.synced} plans synchronized.`);
        this.fetchPlans();
      },
      error: error => {
        this.alertService.error(error);
        this.loading = false;
      }
    });
  }
  private applyFormDefaults(): void {
    this.planForm.reset({
      planId: '',
      planAmount: '',
      planGstAmount: '',
      planNetAmount: '',
      planTotalAmount: '',
      planMaximumAmount: '',
      planMinimumAmount: 0,
      productRackRate: 0,
      operatingCost: 0,
      retentionCost: 0,
      netBenefit: 0,
      policyType: '',
      gstApplicable: 'Y',
      gstShow: 'Y',
      printCpaPolicy: 'Yes',
      planCPACoverAmount: '',
      printRsaNumber: 'No',
      counterId: '',
      masterPolicyNumber: '',
      certificatePrefix: '',
      rsaNumberOfServices: 0,
      numberOfYears: 1,
      applicableOnlyOnPolicyType: 'Both',
      planType: 'General'
    });
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.planForm.invalid || this.costExceedsProduct) {
      return;
    }
    const planData = new PlanDetails(this.planForm.getRawValue());
    const index = this.planSummaries.findIndex(summary => summary.plan.planId === planData.planId);
    if (index > -1) {
      this.planSummaries[index] = {
        ...this.planSummaries[index],
        plan: planData,
        planId: planData.planId
      };
    } else {
      this.planSummaries.push({
        planId: planData.planId,
        plan: planData,
        dealerAssignments: [],
        lastCertificate: null
      });
    }
    this.persistPlans('Plan saved successfully.');
  }
  onEdit(plan: PlanDetails): void {
    this.isEdit = true;
    this.showPlanForm = true;
    this.submitted = false;
    this.planForm.patchValue(plan);
  }
  onDelete(plan: PlanViewModel): void {
    if (!plan?.planId) {
      return;
    }
    this.loading = true;
    this.configService.deletePlan(plan.planId).pipe(first()).subscribe({
      next: () => {
        this.alertService.success(`Plan ${plan.planId} deleted successfully.`);
        this.fetchPlans();
      },
      error: error => {
        this.alertService.error(error);
        this.loading = false;
      }
    });
  }
  startAddPlan(): void {
    this.isEdit = false;
    this.showPlanForm = true;
    this.submitted = false;
    this.applyFormDefaults();
  }

  cancelPlanForm(): void {
    this.resetForm();
  }
  get f() { return this.planForm.controls; }
  resetForm(): void {
    this.isEdit = false;
    this.submitted = false;
    this.showPlanForm = false;
    this.applyFormDefaults();
  }

  openDrilldown(plan: PlanViewModel): void {
    this.drilldown = {
      visible: true,
      plan
    };
  }

  closeDrilldown(): void {
    this.drilldown = { visible: false, plan: null };
  }

  openAssignment(plan: PlanViewModel, mode: 'add' | 'replace' = 'add'): void {
    this.assignment.plan = plan;
    this.assignment.mode = mode;
    this.assignment.replacePlanId = '';
    this.assignment.selectedDealerIds = new Set<string>();
    this.assignment.visible = true;
    this.assignment.submitting = false;
    this.dealerSearch = '';
    this.prepareAssignmentDealers(plan);
  }

  closeAssignment(): void {
    this.assignment.visible = false;
    this.assignment.plan = null;
    this.assignment.selectedDealerIds = new Set<string>();
    this.assignment.sourcePlan = null;
    this.assignment.showConfirmation = false;
    this.planComparisonFields = [];
    this.hasAmountDifferences = false;
  }

  toggleDealerSelection(dealerId: string, checked: boolean): void {
    if (checked) {
      this.assignment.selectedDealerIds.add(dealerId);
    } else {
      this.assignment.selectedDealerIds.delete(dealerId);
    }
  }

  isDealerSelected(dealerId: string): boolean {
    return this.assignment.selectedDealerIds.has(dealerId);
  }

  /**
   * Check if a dealer should be disabled for selection.
   * - Add mode: disable if dealer already has the target plan
   * - Replace mode: disable if dealer does NOT have the source plan
   */
  isDealerDisabled(option: DealerOption): boolean {
    if (this.assignment.mode === 'add') {
      return option.hasPlan; // Can't add if already has target plan
    } else {
      // Replace mode: can only select dealers who HAVE the source plan
      return !option.hasPlan; // Disable if doesn't have source plan
    }
  }

  /**
   * Get status text for dealer based on mode.
   */
  getDealerStatusText(option: DealerOption): string {
    if (this.assignment.mode === 'add') {
      return option.hasPlan ? 'Already has plan' : 'Not assigned';
    } else {
      // Replace mode
      return option.hasPlan ? 'Has plan (eligible)' : 'Does not have plan';
    }
  }

  toggleAllVisibleDealers(checked: boolean): void {
    const visibleDealers = this.filteredAssignmentDealers;
    visibleDealers.forEach(option => {
      if (checked) {
        // Skip if dealer is disabled (not eligible for this operation)
        if (this.isDealerDisabled(option)) {
          return;
        }
        this.assignment.selectedDealerIds.add(option.dealerId);
      } else {
        this.assignment.selectedDealerIds.delete(option.dealerId);
      }
    });
  }

  get filteredAssignmentDealers(): DealerOption[] {
    if (!this.assignmentDealerOptions) {
      return [];
    }
    
    let options = this.assignmentDealerOptions;
    
    // In replace mode, only show dealers who have the source plan
    if (this.assignment.mode === 'replace') {
      options = options.filter(option => option.hasPlan);
    }
    
    const search = this.dealerSearch.trim().toLowerCase();
    if (!search) {
      return options;
    }
    return options.filter(option =>
      option.dealerName.toLowerCase().includes(search) ||
      option.dealerId.toLowerCase().includes(search)
    );
  }

  get selectableVisibleDealerCount(): number {
    return this.filteredAssignmentDealers.filter(option => !this.isDealerDisabled(option)).length;
  }

  get filteredPlans(): PlanViewModel[] {
    const search = this.planSearchTerm.trim().toLowerCase();
    return this.plans.filter(plan => {
      const planId = (plan.planId || '').toLowerCase();
      const policyLabel = this.getPolicyTypeLabel(plan);
      const policy = policyLabel.toLowerCase();
      const companyLabel = this.getInsuranceCompanyLabel(plan);
      const company = companyLabel.toLowerCase();
      const matchesSearch = !search ||
        planId.includes(search) ||
        policy.includes(search) ||
        company.includes(search);
      const matchesPolicy = !this.policyTypeFilter || policyLabel === this.policyTypeFilter;
      const matchesCompany = !this.insuranceCompanyFilter || companyLabel === this.insuranceCompanyFilter;
      return matchesSearch && matchesPolicy && matchesCompany;
    });
  }

  get policyTypeOptions(): string[] {
    const values = new Set<string>();
    this.plans.forEach(plan => values.add(this.getPolicyTypeLabel(plan)));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }

  private loadPolicyCompanies(): void {
    this.configService.PolicyCompanies$.subscribe({
      next: (companies: string[]) => {
        this.policyCompanies = companies || [];
      },
      error: (error) => {
        // Clear policy companies array - the getter will fallback to deriving from plans
        this.policyCompanies = [];
        this.alertService.warn('Failed to load policy companies from config, will use values from plans');
      }
    });
  }

  get insuranceCompanyOptions(): string[] {
    // Use policy companies from config if available, otherwise derive from plans
    if (this.policyCompanies && this.policyCompanies.length > 0) {
      return this.policyCompanies;
    }
    // Fallback: derive from plans
    const values = new Set<string>();
    this.plans.forEach(plan => values.add(this.getInsuranceCompanyLabel(plan)));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }

  clearPlanFilters(): void {
    this.planSearchTerm = '';
    this.policyTypeFilter = '';
    this.insuranceCompanyFilter = '';
  }

  setAssignmentMode(mode: 'add' | 'replace'): void {
    if (this.assignment.mode !== mode) {
      this.assignment.mode = mode;
      this.assignment.replacePlanId = '';
      this.assignment.selectedDealerIds.clear();
      if (this.assignment.plan && this.allDealersCache) {
        // In add mode, show dealers based on target plan
        // In replace mode, dealer options will update when source plan is selected
        if (mode === 'add') {
          this.assignmentDealerOptions = this.decorateDealerOptions(this.assignment.plan, this.allDealersCache);
        } else {
          // Clear hasPlan until source plan is selected
          this.assignmentDealerOptions = this.allDealersCache.map(option => ({
            ...option,
            hasPlan: false
          }));
        }
      }
    }
  }

  onReplacePlanChange(planId: string): void {
    this.assignment.replacePlanId = planId;
    this.assignment.selectedDealerIds.clear();
    this.assignment.showConfirmation = false;
    
    // Refresh dealer list to show dealers who have the SOURCE plan being replaced
    if (planId && this.allDealersCache) {
      const sourcePlan = this.plans.find(p => p.planId === planId);
      if (sourcePlan) {
        this.assignment.sourcePlan = sourcePlan;
        this.assignmentDealerOptions = this.decorateDealerOptionsForReplace(sourcePlan, this.allDealersCache);
        // Build comparison between source and target plans
        this.buildPlanComparison(sourcePlan, this.assignment.plan);
      }
    } else {
      this.assignment.sourcePlan = null;
      this.planComparisonFields = [];
      this.hasAmountDifferences = false;
    }
  }

  /**
   * Build side-by-side comparison of source and target plans.
   * Highlights amount field differences.
   */
  private buildPlanComparison(sourcePlan: PlanViewModel | null, targetPlan: PlanViewModel | null): void {
    if (!sourcePlan || !targetPlan) {
      this.planComparisonFields = [];
      this.hasAmountDifferences = false;
      return;
    }

    // Fields that will be preserved from dealer's existing plan during admin replacement
    const preservedFields = [
      'planAmount', 'planGstAmount', 'planTotalAmount', 'planNetAmount', 
      'planMinimumAmount', 'planMaximumAmount', 'gstApplicable', 'gstShow',
      'applicableOnlyOnPolicyType', 'operatingCost'
    ];
    
    const fieldLabels: { [key: string]: string } = {
      planId: 'Plan ID',
      planAmount: 'Plan Amount ⚡',
      planGstAmount: 'GST Amount ⚡',
      planTotalAmount: 'Total Amount ⚡',
      planNetAmount: 'Net Amount ⚡',
      planMinimumAmount: 'Minimum Amount ⚡',
      planMaximumAmount: 'Maximum Amount ⚡',
      operatingCost: 'Operating Cost ⚡',
      planCPACoverAmount: 'CPA Cover Amount',
      policyType: 'Policy Type',
      gstApplicable: 'GST Applicable ⚡',
      gstShow: 'GST Show ⚡',
      printCpaPolicy: 'Print CPA Policy',
      printRsaNumber: 'Print RSA Number',
      rsaNumberOfServices: 'RSA Services',
      numberOfYears: 'Number of Years',
      counterId: 'Counter ID',
      masterPolicyNumber: 'Master Policy',
      certificatePrefix: 'Certificate Prefix',
      applicableOnlyOnPolicyType: 'Applicable On ⚡'
    };

    const fieldsToCompare = Object.keys(fieldLabels);
    this.planComparisonFields = [];
    this.hasAmountDifferences = false;

    for (const field of fieldsToCompare) {
      const sourceValue = (sourcePlan as any)[field];
      const targetValue = (targetPlan as any)[field];
      const isPreserved = preservedFields.includes(field);
      const hasDifference = this.valuesAreDifferent(sourceValue, targetValue);

      if (isPreserved && hasDifference) {
        this.hasAmountDifferences = true;
      }

      this.planComparisonFields.push({
        label: fieldLabels[field],
        field,
        sourceValue: this.formatFieldValue(sourceValue, isPreserved),
        targetValue: this.formatFieldValue(targetValue, isPreserved),
        isAmount: isPreserved, // renamed but keeping interface compatible
        hasDifference
      });
    }
  }

  private valuesAreDifferent(a: any, b: any): boolean {
    if (a === b) return false;
    if (a == null && b == null) return false;
    if (typeof a === 'number' && typeof b === 'number') {
      return Math.abs(a - b) > 0.001;
    }
    return String(a || '').trim() !== String(b || '').trim();
  }

  private formatFieldValue(value: any, isAmount: boolean): string {
    if (value == null || value === '') return '—';
    if (isAmount && typeof value === 'number') {
      return value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return String(value);
  }

  /**
   * Submit plan assignment from admin screen.
   * 
   * Behavior:
   * - 'add' mode: New plan is assigned with ALL values from the plan template
   * - 'replace' mode: Plan is replaced, but financial values are preserved
   *   (planAmount, planGstAmount, planTotalAmount, planNetAmount, gstApplicable, gstShow, planMinimumAmount, operatingCost)
   */
  submitAssignment(): void {
    if (!this.assignment.plan || this.assignment.selectedDealerIds.size === 0) {
      this.alertService.error('Please select at least one dealer.');
      return;
    }
    if (this.assignment.mode === 'replace' && !this.assignment.replacePlanId) {
      this.alertService.error('Select the plan you want to replace.');
      return;
    }
    
    // If there are amount differences and user hasn't confirmed yet, show confirmation
    if (this.assignment.mode === 'replace' && this.hasAmountDifferences && !this.assignment.showConfirmation) {
      this.assignment.showConfirmation = true;
      return;
    }
    
    this.executeAssignment();
  }

  /**
   * Cancel the confirmation and go back to selection.
   */
  cancelConfirmation(): void {
    this.assignment.showConfirmation = false;
  }

  /**
   * Execute the actual plan assignment after validation/confirmation.
   */
  executeAssignment(): void {
    const payload = {
      targetPlanId: this.assignment.plan!.planId,
      dealerIds: Array.from(this.assignment.selectedDealerIds),
      operation: this.assignment.mode,
      sourcePlanId: this.assignment.mode === 'replace' ? this.assignment.replacePlanId : undefined,
      // From admin screen: preserve financial values when replacing
      source: 'admin' as const
    };
    this.assignment.submitting = true;
    this.planMaintainService.bulkAssignPlan(payload).pipe(first()).subscribe({
      next: response => {
        this.assignment.submitting = false;
        const skipped = response.skipped?.length || 0;
        let successMessage = '';
        if (this.assignment.mode === 'add') {
          successMessage = skipped
            ? `${response.updatedDealers} dealer(s) assigned new plan. ${skipped} skipped.`
            : `${response.updatedDealers} dealer(s) assigned new plan successfully.`;
        } else {
          successMessage = skipped
            ? `${response.updatedDealers} dealer(s) updated (financials preserved). ${skipped} skipped.`
            : `${response.updatedDealers} dealer(s) updated successfully (financials preserved).`;
        }
        this.alertService.success(successMessage);
        this.closeAssignment();
        this.fetchPlans();
      },
      error: error => {
        this.assignment.submitting = false;
        this.alertService.error(error);
      }
    });
  }

  private prepareAssignmentDealers(plan: PlanViewModel): void {
    if (this.allDealersCache) {
      this.assignmentDealerOptions = this.decorateDealerOptions(plan, this.allDealersCache);
      return;
    }
    this.isLoadingDealers = true;
    this.dealerService.getAllDealer().pipe(first()).subscribe({
      next: (dealers: Dealer[]) => {
        this.isLoadingDealers = false;
        this.allDealersCache = (dealers || []).map(dealer => ({
          dealerId: dealer.dealerId,
          dealerName: dealer.dealerName,
          dealerPlanType: dealer.dealerPlanType,
          hasPlan: false
        }));
        this.assignmentDealerOptions = this.decorateDealerOptions(plan, this.allDealersCache);
      },
      error: error => {
        this.isLoadingDealers = false;
        this.alertService.error(error);
        this.assignment.visible = false;
      }
    });
  }

  private decorateDealerOptions(plan: PlanViewModel, baseOptions: DealerOption[]): DealerOption[] {
    const dealerIdSet = new Set((plan.dealerAssignments || []).map(d => d.dealerId));
    return baseOptions.map(option => ({
      ...option,
      hasPlan: dealerIdSet.has(option.dealerId)
    }));
  }

  /**
   * For replace mode: mark dealers who have the SOURCE plan (plan being replaced).
   * In replace mode, we want to show dealers who HAVE the source plan so they can be selected for replacement.
   */
  private decorateDealerOptionsForReplace(sourcePlan: PlanViewModel, baseOptions: DealerOption[]): DealerOption[] {
    const dealerIdSet = new Set((sourcePlan.dealerAssignments || []).map(d => d.dealerId));
    return baseOptions.map(option => ({
      ...option,
      hasPlan: dealerIdSet.has(option.dealerId) // true = dealer has the source plan (eligible for replacement)
    }));
  }

  get replacementPlanOptions(): PlanViewModel[] {
    return this.plans.filter(p => !this.assignment.plan || p.planId !== this.assignment.plan.planId);
  }

  private persistPlans(successMessage?: string): void {
    const sanitizedPlans = this.planSummaries.map(summary => new PlanDetails(summary.plan));
    this.updatePlan.ResultValue = sanitizedPlans;
    this.loading = true;
    this.configService.resetStringCacheConfig(this.updatePlan).pipe(first()).subscribe({
      next: () => {
        // Sync the dealer-plan mapping cache after plan catalog changes
        this.configService.syncPlanDealerMappings().pipe(first()).subscribe({
          next: () => {
            if (successMessage) {
              this.alertService.success(successMessage);
            }
            this.resetForm();
            this.fetchPlans();
          },
          error: () => {
            // Cache sync failed but plan was saved
            if (successMessage) {
              this.alertService.success(successMessage + ' (Cache sync pending)');
            }
            this.resetForm();
            this.fetchPlans();
          }
        });
      },
      error: error => {
        this.alertService.error(error);
        this.loading = false;
      }
    });
  }

  private buildPlanViewModels(): void {
    const plans = this.planSummaries.map(summary => {
      const plan = summary.plan;
      const matchingInstruction = this.printInstructions.find(
        instruction => instruction.policyType === plan.policyType
      ) || null;
      return {
        ...plan,
        dealerAssignments: summary.dealerAssignments || [],
        lastCertificate: summary.lastCertificate,
        dealerCount: summary.dealerAssignments ? summary.dealerAssignments.length : 0,
        printInstruction: matchingInstruction
      } as PlanViewModel;
    });

    this.plans = plans.sort((a, b) => {
      const companyA = a.printInstruction?.policyCompany || '';
      const companyB = b.printInstruction?.policyCompany || '';
      return companyA.localeCompare(companyB);
    });

    this.ensureFilterIntegrity();
    this.updatePlan.ResultValue = this.planSummaries.map(summary => summary.plan);
  }

  private getPolicyTypeLabel(plan: PlanViewModel): string {
    return (plan.policyType && plan.policyType.trim().length > 0)
      ? plan.policyType.trim()
      : 'Unassigned';
  }

  private getInsuranceCompanyLabel(plan: PlanViewModel): string {
    const company = plan.printInstruction?.policyCompany || '';
    return company.trim().length > 0 ? company.trim() : 'Unassigned';
  }

  private ensureFilterIntegrity(): void {
    const policyOptions = new Set(this.policyTypeOptions);
    if (this.policyTypeFilter && !policyOptions.has(this.policyTypeFilter)) {
      this.policyTypeFilter = '';
    }
    const companyOptions = new Set(this.insuranceCompanyOptions);
    if (this.insuranceCompanyFilter && !companyOptions.has(this.insuranceCompanyFilter)) {
      this.insuranceCompanyFilter = '';
    }
  }
}
